// Design tokens matching the prototypeNew/ HTML reference (frontend/prototypeNew).
// Centralized here so every rebuilt page pulls from one source instead of
// repeating hex codes, keeping the new design system consistent app-wide.

export const colors = {
  primary: "#1877F2",
  primaryDark: "#0C44AE",
  primaryLighter: "#D0ECFE",
  primarySoft: "#EAF3FF",
  secondary: "#8E33FF",
  success: "#22C55E",
  successDark: "#118D57",
  successSoft: "#E9FBEA",
  warning: "#FFAB00",
  warningDark: "#B76E00",
  warningSoft: "#FFF7E0",
  error: "#FF5630",
  errorDark: "#B71D18",
  errorSoft: "#FFEEEA",
  info: "#00B8D9",
  infoDark: "#006C9C",
  infoSoft: "#E5FAFD",
  grey50: "#FCFDFD",
  grey100: "#F9FAFB",
  grey150: "#F1F4F7",
  grey200: "#F4F6F8",
  grey300: "#DFE3E8",
  grey400: "#C4CDD5",
  grey500: "#919EAB",
  grey600: "#637381",
  grey700: "#454F5B",
  grey800: "#1C252E",
  grey900: "#141A21",
  navy: "#242c54",
  navyDark: "#1b2140",
  paper: "#FFFFFF",
};

// Categorical series palette used for the artifact-distribution donut/legend.
export const seriesColors = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#4a3aa7",
];

export const radius = { sm: 8, md: 12, lg: 16, pill: 999 };

export const shadow = {
  1: "0 1px 2px rgba(20,26,33,.06), 0 1px 1px rgba(20,26,33,.04)",
  2: "0 4px 12px rgba(20,26,33,.08)",
  3: "0 12px 32px rgba(20,26,33,.14)",
};

// Reusable sx fragments for the "card" / "interactive card" look used throughout
// the prototype (KPI tiles, chart cards, asset cards, etc.).
export const cardSx = {
  background: colors.paper,
  borderRadius: `${radius.lg}px`,
  border: `1px solid ${colors.grey200}`,
  boxShadow: shadow[1],
  p: 3,
};

export const interactiveCardSx = {
  ...cardSx,
  transition: "box-shadow .2s cubic-bezier(.2,.8,.2,1), transform .2s cubic-bezier(.2,.8,.2,1)",
  "&:hover": {
    boxShadow: shadow[2],
    transform: "translateY(-2px)",
  },
};

export const kpiIconSx = {
  blue: { background: colors.primarySoft, color: colors.primaryDark },
  green: { background: colors.successSoft, color: colors.successDark },
  amber: { background: colors.warningSoft, color: colors.warningDark },
  cyan: { background: colors.infoSoft, color: colors.infoDark },
  red: { background: colors.errorSoft, color: colors.errorDark },
};

// Status chip look used for artifact status columns/badges (active/pending/inactive/rejected).
export const statusChipSx = {
  1: { background: colors.successSoft, color: colors.successDark, dot: colors.successDark }, // Active
  2: { background: colors.warningSoft, color: colors.warningDark, dot: colors.warningDark }, // Pending
  3: { background: colors.grey150, color: colors.grey700, dot: colors.grey500 }, // Inactive
  4: { background: colors.errorSoft, color: colors.errorDark, dot: colors.errorDark }, // Rejected
};

const FILE_TYPE_COLORS = {
  pdf: colors.primary,
  doc: colors.secondary,
  docx: colors.secondary,
  ppt: colors.error,
  pptx: colors.error,
  xls: colors.successDark,
  xlsx: colors.successDark,
  mp4: colors.infoDark,
  mov: colors.infoDark,
  zip: colors.grey700,
};

// Derives a short file-type badge (label + color) from a filename, falling
// back to a generic tile when no filename/extension is known.
export function getFileTypeMeta(fileName) {
  const ext = (fileName || "").split(".").pop()?.toLowerCase();
  if (!ext || ext === fileName?.toLowerCase()) {
    return { label: "FILE", color: colors.grey600 };
  }
  return { label: ext.slice(0, 4).toUpperCase(), color: FILE_TYPE_COLORS[ext] || colors.grey600 };
}
